import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const NAV_ITEMS = [
  { label: '首页', path: '/' },
  { label: '🦊 知知', path: '/zhi', highlight: true },
  { label: '工具', path: null, hasDropdown: true },
  { label: '图解', path: '/learn' },
  { label: '会员', path: '/membership' },
  { label: '历史', path: '/history' },
  { label: '关于', path: '/about' },
  { label: '联系', path: '/contact' },
];

const MORE_ITEMS = [
  { label: '🔔 通知设置', path: '/notifications' },
  { label: '📊 数据看板', path: '/admin' },
  { label: '📋 服务条款', path: '/terms' },
  { label: '🗄️ 数据管理', path: '/data-management' },
];

// 会员类型标签映射
const MEMBER_LABELS = {
  single: { text: '单次', color: 'bg-blue-100 text-blue-700' },
  quarterly: { text: '季度', color: 'bg-amber-100 text-amber-700' },
  year: { text: '年度', color: 'bg-red-100 text-red-700' },
};

export default function Navbar() {
  const location = useLocation();
  const { user, logout, membershipType, isPaidMember, isMember, isSingle, isFree, membershipInfo, singleUnlocked, isAdmin } = useAuth();
  const visibleMoreItems = MORE_ITEMS.filter(item => item.path !== '/admin' || isAdmin);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hoverMember, setHoverMember] = useState(false);
  const hoverRef = useRef(null);

  // 路由变化时关闭菜单
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  // 会员悬浮提示文案
  const getMemberTooltip = () => {
    if (!user) return '';
    if (membershipType === 'quarterly') {
      return `季度会员 · ${membershipInfo.days_left != null ? `剩余 ${membershipInfo.days_left} 天` : '生效中'}`;
    }
    if (membershipType === 'year') {
      return `年度会员 · ${membershipInfo.days_left != null ? `剩余 ${membershipInfo.days_left} 天` : '生效中'}`;
    }
    if (membershipType === 'single') {
      return singleUnlocked ? `已解锁1个板块 · 升级会员解锁全部` : '可解锁1个板块 · 升级会员解锁全部';
    }
    return '';
  };

  // ============ 右上角用户区 ============
  const renderUserArea = () => {
    if (!user) {
      // 未登录：[⭐ 登录]
      return (
        <Link
          to="/login"
          className="hidden sm:inline-flex items-center gap-1.5 px-5 py-2 text-sm font-medium bg-gradient-to-r from-zhusha to-red-700 text-white rounded-lg hover:shadow-lg hover:scale-105 transition-all shadow-sm"
        >
          <span>⭐</span> 登录
        </Link>
      );
    }

    // 已登录用户：统一显示用户头像 + hover 菜单
    const icon = membershipType === 'year' ? '👑' : membershipType === 'quarterly' ? '🌟' : '👤';
    const label = MEMBER_LABELS[membershipType] || {};
    return (
      <div
        ref={hoverRef}
        className="hidden sm:flex items-center gap-2 relative"
        onMouseEnter={() => setHoverMember(true)}
        onMouseLeave={() => setHoverMember(false)}
      >
        <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors">
          <span className="text-lg">{icon}</span>
          <span className="text-xs text-stone max-w-[80px] truncate">{user.username || '用户'}</span>
          <svg className="w-3 h-3 text-stone/40" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </button>
        {/* hover 下拉菜单 */}
        {hoverMember && (
          <div className="absolute top-full mt-1 right-0 bg-white rounded-xl shadow-xl border border-stone/10 py-2 z-50 min-w-[160px]">
            <div className="absolute -top-1.5 right-4 w-3 h-3 bg-white rotate-45 border-l border-t border-stone/10" />
            {(isFree || isSingle) && (
              <Link to="/membership" className="flex items-center gap-2 px-4 py-2.5 text-xs text-zhusha hover:bg-zhusha/5 transition-colors">
                <span>💎</span> {isFree ? '升级会员' : '解锁全部'}
              </Link>
            )}
            {isMember && (
              <div className="px-4 py-2.5">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${label.color || 'bg-amber-100 text-amber-700'}`}>
                  当前：{label.text || membershipType}
                </span>
                <div className="mt-1.5 text-[10px] text-stone/40">{getMemberTooltip()}</div>
              </div>
            )}
            {(isFree || isSingle) && <div className="border-t border-stone/50 mx-2" />}
            <button
              onClick={() => { logout(); setHoverMember(false); }}
              className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-xs text-stone/50 hover:text-zhusha hover:bg-gray-50 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
              退出登录
            </button>
          </div>
        )}
      </div>
    );
  };

  // ============ 移动端用户区 ============
  const renderMobileUserArea = () => {
    if (!user) {
      return (
        <div className="flex gap-2">
          <Link to="/login" onClick={() => setMenuOpen(false)} className="flex-1 text-center px-4 py-2 border border-stone/20 rounded-lg text-sm text-stone hover:bg-stone/5 transition-colors">
            ⭐ 登录
          </Link>
          <Link to="/membership" onClick={() => setMenuOpen(false)} className="flex-1 text-center px-4 py-2 bg-gradient-to-r from-zhusha to-red-800 text-white text-sm rounded-lg">
            💎 会员
          </Link>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isMember && <span className="text-xs">{membershipType === 'year' ? '👑' : '🌟'}</span>}
            {isSingle && <span className="text-xs">💎</span>}
            <span className="text-sm text-stone">{user.username}</span>
          </div>
          <button onClick={() => { logout(); setMenuOpen(false); }} className="text-xs text-stone/50 hover:text-zhusha">
            退出
          </button>
        </div>
        {!isMember && (
          <Link
            to="/membership"
            onClick={() => setMenuOpen(false)}
            className="block text-center w-full px-4 py-2 bg-gradient-to-r from-zhusha to-red-800 text-white text-sm rounded-lg"
          >
            {isFree ? '💎 升级会员' : '💎 解锁全部'}
          </Link>
        )}
      </div>
    );
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-stone/10 shadow-sm">
      <div className="max-w-4xl mx-auto px-3 sm:px-4">
        <div className="flex items-center justify-between h-12">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-7 h-7 bg-zhusha rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-serif">命</span>
            </div>
            <span className="font-serif font-bold text-ink text-sm hidden sm:inline">命盘职引</span>
          </Link>

          {/* 桌面导航 */}
          <div className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map((item) => {
              if (item.hasDropdown) {
                return (
                  <Link
                    key="工具"
                    to="/tools"
                    className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1 ${
                      isActive('/tools') ? 'bg-stone/10 text-ink' : 'text-stone hover:text-ink hover:bg-stone/5'
                    }`}
                  >
                    工具
                  </Link>
                );
              }
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    isActive(item.path)
                      ? (item.highlight ? 'bg-amber-100 text-amber-700 font-medium' : 'bg-stone/10 text-ink font-medium')
                      : (item.highlight ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50' : 'text-stone hover:text-ink hover:bg-stone/5')
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            {/* 更多下拉 */}
            <div className="relative group">
              <button className="px-3 py-1.5 text-sm rounded-lg transition-colors text-stone hover:text-ink hover:bg-stone/5 flex items-center gap-1">
                更多
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-stone/10 py-1.5 min-w-[160px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                {visibleMoreItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="block px-4 py-2 text-sm text-stone hover:bg-stone/5 hover:text-ink transition-colors whitespace-nowrap"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* 右侧：会员/用户/移动端菜单 */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* 显示用户名（所有已登录用户） */}
            {user && (
              <span className="hidden sm:inline text-xs text-stone/60 max-w-[80px] truncate">{user.username}</span>
            )}

            {renderUserArea()}

            {/* 移动端汉堡菜单 */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden p-1.5 text-stone hover:text-ink transition-colors"
              aria-label="菜单"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {menuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                }
              </svg>
            </button>
          </div>
        </div>

        {/* 移动端菜单面板 */}
        {menuOpen && (
          <div className="md:hidden border-t border-stone/10 py-3 space-y-1">
            {NAV_ITEMS.map((item) => {
              if (item.hasDropdown) {
                return (
                  <Link
                    key="工具-mobile"
                    to="/tools"
                    onClick={() => setMenuOpen(false)}
                    className="block w-full px-3 py-2 rounded-lg text-sm text-left transition-colors text-stone hover:bg-stone/5"
                  >
                    工具 <span className="text-stone/30 float-right">10项 →</span>
                  </Link>
                );
              }
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMenuOpen(false)}
                  className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive(item.path)
                      ? (item.highlight ? 'bg-amber-100 text-amber-700 font-medium' : 'bg-stone/10 text-ink font-medium')
                      : (item.highlight ? 'text-amber-600 hover:bg-amber-50' : 'text-stone hover:bg-stone/5')
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            <div className="border-t border-stone/10 my-1" />
            {visibleMoreItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMenuOpen(false)}
                className="block px-3 py-2 rounded-lg text-sm text-stone/60 hover:bg-stone/5 transition-colors"
              >
                {item.label}
              </Link>
            ))}
            {/* 移动端用户区 */}
            <div className="border-t border-stone/10 pt-2 mt-2 px-3">
              {renderMobileUserArea()}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
